import * as React from 'react';
import clsx from 'clsx';
import { SocialButtons } from '@/components/auth/SocialButtons';
import type { AuthMode } from '@/components/layout/AuthLayout';
import { useVScaleVars } from '@/hooks/useVScale';
import { AuthPromoBlock } from '@/components/layout/auth/AuthPromoBlock';
import { getUtmEchoConfig } from '@/components/layout/auth/utmEchoConfig';
import { AuthMobileHeader } from '@/components/layout/auth/AuthMobileHeader';
import { AuthGreetingText } from '@/components/layout/auth/AuthGreetingText';
import { AuthTitleBlock } from '@/components/layout/auth/AuthTitleBlock';
import { AuthDivider } from '@/components/layout/auth/AuthDivider';
import { AuthSubmitButton } from '@/components/layout/auth/AuthSubmitButton';
import { AuthLinkWrapper } from '@/components/layout/auth/AuthLinkWrapper';
import { AuthChatPreviewCompact } from '@/components/layout/auth/AuthChatPreviewCompact';
import { Button } from '@/components/ui/Button';
import { isSocialAuthConfigured } from '@/utils/socialAuthConfig';

/** @typedef AuthFormPanelProps — Props for the auth form panel (login/register). */
interface AuthFormPanelProps {
  children: React.ReactNode;
  authType: AuthMode;
  onSwitch: () => void;
  isLoading: boolean;
  submitLabel: string;
  dividerText: string;
  formId: string;
  hasUtm: boolean;
  utmEcho: string | null;
  isAuthenticated?: boolean;
  onChatNow?: (() => void) | undefined;
}

const SPACING = {
  headerMb: 'mb-[var(--vs-27,27px)] fold:mb-4',
  socialMb: 'mb-[var(--vs-24,24px)] fold:mb-4',
  dividerMb: 'mb-[var(--vs-24,24px)] fold:mb-4',
  footerMt: 'mt-[var(--vs-16,16px)] fold:mt-6',
  pad: 'px-[var(--vs-15,15px)] pb-[var(--vs-35,35px)] pt-[var(--vs-15,15px)]',
};

/** Tighter spacing for register on mobile (more fields to fit). */
const REGISTER_SPACING: typeof SPACING = {
  headerMb: 'mb-0 fold:mb-4',
  socialMb: 'mb-[var(--vs-8,8px)] fold:mb-4',
  dividerMb: 'mb-[var(--vs-8,8px)] fold:mb-4',
  footerMt: 'mt-[var(--vs-8,8px)] fold:mt-6',
  pad: 'px-[var(--vs-15,15px)] pb-[var(--vs-15,15px)] pt-[var(--vs-8,8px)]',
};

/** Right-side form panel for auth screens (social login, form fields, submit). */
export const AuthFormPanel: React.FC<AuthFormPanelProps> = ({
  children,
  authType,
  onSwitch,
  isLoading,
  submitLabel,
  dividerText,
  formId,
  hasUtm,
  utmEcho,
  isAuthenticated = false,
  onChatNow,
}) => {
  const isRegister = authType === 'register';
  const [promoOnly, setPromoOnly] = React.useState(hasUtm);
  const utmConfig = getUtmEchoConfig(utmEcho);
  const vsVars = useVScaleVars();

  const sp = isRegister ? REGISTER_SPACING : SPACING;

  const SocialSection = isSocialAuthConfigured ? (
    <div className={clsx('w-full', sp.socialMb)}>
      <SocialButtons />
    </div>
  ) : null;

  const isMobileCollapsed = promoOnly && hasUtm;

  return (
    <div
      style={vsVars}
      className={clsx(
        'flex w-full flex-col overflow-y-auto',
        'items-center',
        isMobileCollapsed ? 'py-[var(--vs-15,15px)]' : sp.pad,
        'lg:p-0',
        'lg:flex-1',
        '4xl:w-auto 4xl:flex-1'
      )}
    >
      <div className='flex min-h-full w-full flex-col 4xl:max-w-[27.5rem]'>
        {isMobileCollapsed && (
          <div className='flex flex-1 flex-col justify-start fold:hidden'>
            <div className='mb-3 flex w-full shrink-0 items-center justify-start gap-14 px-[15px] [&_a_img]:!size-[24px] [&_img:first-child]:h-[44px] [&_img:first-child]:w-auto'>
              <AuthMobileHeader />
            </div>
            <div className='px-[15px]'>
              <AuthPromoBlock {...utmConfig.promo} compact />
            </div>
            <AuthChatPreviewCompact
              avatar={utmConfig.avatar}
              name={utmConfig.headerName}
              messages={utmConfig.messages}
            />
            <div className='mt-4 overflow-visible px-[15px] pb-2'>
              <Button
                type='button'
                variant='purple-dark-light-border'
                className='h-[45px] w-full rounded-30 text-16 font-bold uppercase tracking-wide'
                onClick={isAuthenticated ? onChatNow : () => setPromoOnly(false)}
              >
                {isAuthenticated ? 'CHAT NOW' : 'Start Chatting Now'}
              </Button>
            </div>
          </div>
        )}

        <div className={clsx(isMobileCollapsed && 'hidden fold:contents')}>
          <div className={clsx('flex w-full shrink-0 items-center justify-between', sp.headerMb)}>
            <div className={clsx(isRegister ? 'hidden fold:contents' : 'contents')}>
              <AuthMobileHeader />
            </div>
            {!hasUtm && (
              <AuthGreetingText className='hidden text-accent-20 fold:block fold:text-brand-800' />
            )}
          </div>

          <div
            className={clsx(
              'flex w-full flex-1 flex-col',
              !isRegister ? 'justify-center' : 'justify-start'
            )}
          >
            {hasUtm ? (
              <AuthPromoBlock {...utmConfig.promo} />
            ) : (
              <AuthTitleBlock isRegister={isRegister} onSwitch={onSwitch} />
            )}

            {isAuthenticated ? (
              <div className='flex flex-col items-center gap-4 py-4'>
                <Button
                  type='button'
                  variant='purple-dark-light-border'
                  className='h-[52px] w-full rounded-30 text-18 font-bold uppercase tracking-wide'
                  onClick={onChatNow}
                >
                  CHAT NOW
                </Button>
              </div>
            ) : (
              <>
                {SocialSection}
                {isSocialAuthConfigured && (
                  <AuthDivider text={dividerText} className={sp.dividerMb} />
                )}

                <div className='shrink-0'>{children}</div>
              </>
            )}
          </div>

          {!isAuthenticated && (
            <div className={clsx('flex w-full shrink-0 flex-col gap-4', sp.footerMt)}>
              <AuthSubmitButton formId={formId} isLoading={isLoading} label={submitLabel} />

              <div className={clsx(isRegister && 'hidden fold:block')}>
                <AuthLinkWrapper
                  isRegister={isRegister}
                  onSwitch={onSwitch}
                  isLoading={isLoading}
                />
              </div>

              {hasUtm && (
                <AuthGreetingText className='mt-4 text-center text-white fold:text-brand-800' />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
